import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import UnderlineExt from '@tiptap/extension-underline';
import LinkExt from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import {
  Bold, Italic, Underline as UnderlineIcon,
  Heading2, Heading3,
  List, ListOrdered,
  Link2, Unlink2,
  RemoveFormatting,
} from 'lucide-react';
import './LegalEditor.css';

/* ── Palette de couleurs ────────────────────────────────────────────────── */
const COLORS = [
  { hex: '#1e293b', label: 'Noir'   },
  { hex: '#64748b', label: 'Gris'   },
  { hex: '#e2001a', label: 'Rouge'  },
  { hex: '#191998', label: 'Bleu'   },
  { hex: '#16a34a', label: 'Vert'   },
  { hex: '#ea580c', label: 'Orange' },
];

/* ── Icône "A barré" pour réinitialiser la couleur ─────────────────────── */
const ResetColorIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5" aria-hidden="true">
    <text x="2" y="12" fontSize="10" fontWeight="700" fill="currentColor" fontFamily="sans-serif">A</text>
    <line x1="2" y1="14" x2="14" y2="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/* ── Bouton de toolbar texte/icône ──────────────────────────────────────── */
const Btn = ({ onMouseDown, active, title, children }) => (
  <button
    type="button"
    title={title}
    onMouseDown={e => { e.preventDefault(); onMouseDown(); }}
    className={`h-8 w-8 flex items-center justify-center rounded transition-colors ${
      active
        ? 'bg-slate-700 text-white'
        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
    }`}
  >
    {children}
  </button>
);

/* ── Carré de couleur ───────────────────────────────────────────────────── */
const ColorSwatch = ({ hex, label, active, onMouseDown }) => (
  <button
    type="button"
    title={label}
    onMouseDown={e => { e.preventDefault(); onMouseDown(); }}
    className={`w-5 h-5 rounded-sm border-2 transition-transform ${
      active ? 'border-slate-700 scale-110' : 'border-transparent hover:border-slate-400 hover:scale-105'
    }`}
    style={{ backgroundColor: hex }}
  />
);

const Sep = () => <div className="w-px h-5 bg-slate-200 mx-0.5 self-center" />;

/* ── Composant principal ────────────────────────────────────────────────── */
const LegalEditor = ({ content, onChange }) => {
  const [showLink, setShowLink] = useState(false);
  const [linkUrl, setLinkUrl]   = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      UnderlineExt,
      LinkExt.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      TextStyle,
      Color,
    ],
    content,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  if (!editor) return null;

  /* Gestion du lien */
  const handleLink = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      setShowLink(false);
      return;
    }
    setLinkUrl(editor.getAttributes('link').href ?? '');
    setShowLink(true);
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
    setShowLink(false);
    setLinkUrl('');
  };

  const cancelLink = () => {
    setShowLink(false);
    setLinkUrl('');
    editor.chain().focus().run();
  };

  /* Couleur active */
  const activeColor = editor.getAttributes('textStyle').color ?? null;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden focus-within:border-ter-blue focus-within:ring-1 focus-within:ring-ter-blue transition">

      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-slate-50 border-b border-slate-200">

        {/* Mise en forme du texte */}
        <Btn onMouseDown={() => editor.chain().focus().toggleBold().run()}
             active={editor.isActive('bold')} title="Gras (Ctrl+B)">
          <Bold className="w-3.5 h-3.5" />
        </Btn>

        <Btn onMouseDown={() => editor.chain().focus().toggleItalic().run()}
             active={editor.isActive('italic')} title="Italique (Ctrl+I)">
          <Italic className="w-3.5 h-3.5" />
        </Btn>

        <Btn onMouseDown={() => editor.chain().focus().toggleUnderline().run()}
             active={editor.isActive('underline')} title="Souligné (Ctrl+U)">
          <UnderlineIcon className="w-3.5 h-3.5" />
        </Btn>

        <Sep />

        {/* Titres */}
        <Btn onMouseDown={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
             active={editor.isActive('heading', { level: 2 })} title="Titre H2">
          <Heading2 className="w-3.5 h-3.5" />
        </Btn>

        <Btn onMouseDown={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
             active={editor.isActive('heading', { level: 3 })} title="Titre H3">
          <Heading3 className="w-3.5 h-3.5" />
        </Btn>

        <Sep />

        {/* Listes */}
        <Btn onMouseDown={() => editor.chain().focus().toggleBulletList().run()}
             active={editor.isActive('bulletList')} title="Liste à puces">
          <List className="w-3.5 h-3.5" />
        </Btn>

        <Btn onMouseDown={() => editor.chain().focus().toggleOrderedList().run()}
             active={editor.isActive('orderedList')} title="Liste numérotée">
          <ListOrdered className="w-3.5 h-3.5" />
        </Btn>

        <Sep />

        {/* Lien */}
        <Btn onMouseDown={handleLink}
             active={editor.isActive('link')}
             title={editor.isActive('link') ? 'Supprimer le lien' : 'Insérer un lien'}>
          {editor.isActive('link')
            ? <Unlink2 className="w-3.5 h-3.5" />
            : <Link2 className="w-3.5 h-3.5" />}
        </Btn>

        <Sep />

        {/* Palette de couleurs */}
        <div className="flex items-center gap-1 px-0.5">
          {COLORS.map(({ hex, label }) => (
            <ColorSwatch
              key={hex}
              hex={hex}
              label={label}
              active={activeColor === hex}
              onMouseDown={() => editor.chain().focus().setColor(hex).run()}
            />
          ))}
        </div>

        {/* Réinitialiser la couleur */}
        <Btn
          onMouseDown={() => editor.chain().focus().unsetColor().run()}
          active={false}
          title="Réinitialiser la couleur"
        >
          <ResetColorIcon />
        </Btn>

        <Sep />

        {/* Effacer toute la mise en forme */}
        <Btn onMouseDown={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
             active={false} title="Effacer la mise en forme">
          <RemoveFormatting className="w-3.5 h-3.5" />
        </Btn>
      </div>

      {/* ── Saisie URL ── */}
      {showLink && (
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 border-b border-slate-200">
          <span className="text-xs text-slate-500 shrink-0">URL :</span>
          <input
            autoFocus
            type="url"
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
              if (e.key === 'Escape') cancelLink();
            }}
            placeholder="https://…"
            className="flex-1 h-7 px-2 rounded border border-slate-300 focus:border-ter-blue outline-none text-sm bg-white"
          />
          <button
            type="button"
            onClick={applyLink}
            className="h-7 px-3 rounded bg-ter-blue text-white text-xs font-bold hover:brightness-95"
          >
            OK
          </button>
          <button
            type="button"
            onClick={cancelLink}
            className="h-7 px-2 rounded text-slate-500 hover:bg-slate-200 text-xs"
          >
            Annuler
          </button>
        </div>
      )}

      {/* ── Zone d'édition ── */}
      <EditorContent editor={editor} className="px-4 py-3" />
    </div>
  );
};

export default LegalEditor;
